import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { AbstractComponent } from '../../../../commons/abstract.component';
import { ScheduleWeek } from '../../../../models/department.model';
import { SettingsModule } from '../../../../models/settings-module.model';
import { SettingsModuleService } from '../../../../services/settings-module.service';

@Component({
    selector: 'ia-schedule-modal-component',
    templateUrl: './ia-schedule-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IAScheduleModalComponent extends AbstractComponent {

  settingsModule: SettingsModule;

  constructor(
    injector: Injector,
    public settingsModuleService: SettingsModuleService,
    public dialogRef: MatDialogRef<IAScheduleModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SettingsModule,
  ) {
    super(injector);
    this.settingsModule = data;
    this.createForm();
  }

  ngOnInit() {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    days.forEach((day) => {
      const control = this.formGroup.get(day);
      this.isEnabled(day);
      control.get('actived')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.isEnabled(day);
        });
    });

    if (this.settingsModule?.inactivityAISchedules) {
      this.formGroup.patchValue({ ...this.settingsModule.inactivityAISchedules });
    }
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      monday: this.formBuilder.group({
        actived: [false],
        startTime: ['08:00'],
        endTime: ['18:00'],
      }),
      tuesday: this.formBuilder.group({
        actived: [false],
        startTime: ['08:00'],
        endTime: ['18:00'],
      }),
      wednesday: this.formBuilder.group({
        actived: [false],
        startTime: ['08:00'],
        endTime: ['18:00'],
      }),
      thursday: this.formBuilder.group({
        actived: [false],
        startTime: ['08:00'],
        endTime: ['18:00'],
      }),
      friday: this.formBuilder.group({
        actived: [false],
        startTime: ['08:00'],
        endTime: ['18:00'],
      }),
      saturday: this.formBuilder.group({
        actived: [false],
        startTime: ['08:00'],
        endTime: ['18:00'],
      }),
      sunday: this.formBuilder.group({
        actived: [false],
        startTime: ['08:00'],
        endTime: ['18:00'],
      }),
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  save() {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const inactivityAISchedules = {} as ScheduleWeek;

    days.forEach(day => {
      const dayValue = this.formGroup.get(day).value;
      inactivityAISchedules[day] = {
        actived: dayValue.actived,
        startTime: this.formatTime(dayValue.startTime),
        endTime: this.formatTime(dayValue.endTime),
      };
    });

    this.settingsModule.inactivityAISchedules = inactivityAISchedules as ScheduleWeek;

    this.settingsModuleService
      .update(this.settingsModule._id, this.settingsModule)
      .subscribe({
        next: (value) => {
          this.alertService.success(this.t('shared.iaScheduleModal.saveSuccess'));
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.alertService.error(err.error.message);
        },
      });
  }

  isEnabled(value: string): void {
    if (this.formGroup.get(value).get('actived').value) {
      this.formGroup.get(value).get('startTime').setValidators([Validators.required]);
      this.formGroup.get(value).get('endTime').setValidators([Validators.required]);
      this.formGroup.get(value).get('startTime').enable();
      this.formGroup.get(value).get('endTime').enable();
    } else {
      this.formGroup.get(value).get('startTime').clearValidators();
      this.formGroup.get(value).get('endTime').clearValidators();
      this.formGroup.get(value).get('startTime').disable();
      this.formGroup.get(value).get('endTime').disable();
    }
  }

  private formatTime(time: string): string {
    if (!time) return '00:00';
    const cleaned = time.replace(/[^0-9]/g, '');
    if (cleaned.length === 4) {
      return `${cleaned.slice(0, 2)}:${cleaned.slice(2, 4)}`;
    }
    return time.includes(':') ? time : '00:00';
  }
}
